/**
 * Display helpers for Simulint user records (Story 22), shared by the
 * shell's identity badge and the Users page.
 */

import type { Me, PlatformRole, UserScope } from '@bluesphere/api-client';

export const ROLE_LABELS: Record<PlatformRole, string> = {
  admin: 'Admin',
  analyst: 'Analyst',
  viewer: 'Viewer',
};

export const ROLES: PlatformRole[] = ['admin', 'analyst', 'viewer'];

/**
 * A scope in words: "All Clients", or the Client names. An id with no
 * matching Client (still loading, or a Client since deleted — the server
 * drops those on read) is shown shortened rather than hidden.
 */
export function scopeLabel(scope: UserScope, names: Map<string, string>, home?: string): string {
  // `home` once the tree exists (Story 48): unscoped is the home's subtree.
  if (scope === 'all') return home && home !== 'Simulint' ? `All of ${home}` : 'All Clients';
  if (scope.length === 0) return 'No Clients';
  return scope.map((id) => names.get(id) ?? `${id.slice(0, 8)}…`).join(', ');
}

/**
 * The account line (task 208): "Admin · Acme MSP", with "· scoped" for a
 * user limited to named Clients. An MSP's unscoped user carries their
 * MSP's Client ids as scope on the wire, so "scoped" is decided against
 * the Clients under their home (`homeClients`, from the tree). Before the
 * tree exists: "Admin · all Clients", as before.
 */
export function roleLine(me: Me, homeClients?: Set<string>): string {
  const role = ROLE_LABELS[me.role];
  if (!me.home_org) return `${role}${me.scope === 'all' ? ' · all Clients' : ' · scoped'}`;
  const scoped =
    me.scope !== 'all' &&
    (homeClients === undefined || [...homeClients].some((id) => !me.scope.includes(id)));
  return `${role} · ${me.home_org.name ?? 'your organization'}${scoped ? ' · scoped' : ''}`;
}
